import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { AccountCircle, Logout, Menu as MenuIcon, VideoLibrary, WorkspacePremium } from '@mui/icons-material';
import { SignedIn, SignedOut, useUser } from '@clerk/clerk-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { MouseEvent, useState } from 'react';
import Sidebar from './Sidebar';
import { useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserData } from '../../hooks/useUserData';
import { buildCheckoutUrl } from '../../utils/billing';
import PaywallModal from '../common/PaywallModal';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { isDarkMode } = useTheme();
  const { user, isSignedIn } = useUser();
  const { data: userData, error: userDataError, isLoading, isFetching } = useUserData();
  // const { getToken, isSignedIn } = useAuth();
  // const [userData, setUserData] = useState<UserData | null>(null);
  // const [loadingUserData, setLoadingUserData] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const open = Boolean(anchorEl);

  // Fetch user data to check if they have groups
  // useEffect(() => {
  //   const fetchUserData = async () => {
  //     if (!isSignedIn) {
  //       setUserData(null);
  //       return;
  //     }

  //     try {
  //       setLoadingUserData(true);
  //       const token = await getToken();
  //       if (!token) return;

  //       const response = await fetch(USER_ENDPOINTS.USER, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       });

  //       if (response.ok) {
  //         const data: UserData = await response.json();
  //         setUserData(data);
  //       }
  //     } catch (err) {
  //       console.error('Error fetching user data in header:', err);
  //     } finally {
  //       setLoadingUserData(false);
  //     }
  //   };

  //   fetchUserData();
  // }, [getToken, isSignedIn]);

  // is on home, privacy policy or tos page
  const showAppButton =
    location.pathname === '/' || location.pathname === '/privacy-policy' || location.pathname === '/terms-of-service';

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMyAccount = () => {
    navigate('/my-account');
    handleMenuClose();
  };

  const handleUpgrade = () => {
    handleMenuClose();
    setPaywallOpen(true);
  };

  const handleSignOut = () => {
    signOut();
    handleMenuClose();
  };

  const isProUser = userData?.pro;
  const loadingUser = isLoading || (isFetching && !userData);
  const userError = userDataError ? userDataError.message : null;
  const checkoutUrl = buildCheckoutUrl(userData?.billingId);

  const subscriptionTier = isProUser ? 'Premium' : 'Free';

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              <SignedIn>
                <Tooltip title="Open menu">
                  <IconButton
                    onClick={handleSidebarToggle}
                    color="secondary"
                    size="large"
                    sx={{
                      color: 'secondary.main',
                      mr: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              </SignedIn>
              <Box
                component={RouterLink}
                to={isSignedIn ? '/app' : '/'}
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', height: '100%' }}
              >
                <Box
                  component="img"
                  src={isDarkMode ? '/logo_dark_mode.png' : '/logo_light_mode.png'}
                  sx={{
                    height: '38px',
                    width: { xs: '90px', sm: 'auto' },
                    objectFit: { xs: 'cover', sm: 'contain' },
                    objectPosition: 'left',
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SignedOut>
                <Button component={RouterLink} to="/login" variant="contained" color="primary" data-cy="sign-in-button">
                  Sign In
                </Button>
              </SignedOut>

              <SignedIn>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {showAppButton && (
                    <Button
                      component={RouterLink}
                      to="/app"
                      variant="contained"
                      color="primary"
                      startIcon={<VideoLibrary />}
                    >
                      App
                    </Button>
                  )}
                  <Tooltip title="Account menu">
                    <IconButton
                      onClick={handleMenuClick}
                      color="secondary"
                      sx={{
                        p: 0.5,
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: open ? 'primary.main' : 'transparent',
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        transition: 'all 0.2s ease',
                        mr: { xs: 0.5, sm: 1 },
                        ml: { xs: 0.5, sm: 1 },
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.08)',
                        },
                      }}
                      aria-controls={open ? 'user-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                    >
                      <PersonIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="user-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    slotProps={{
                      list: {
                        'aria-labelledby': 'user-button',
                      },
                      paper: {
                        sx: {
                          minWidth: { xs: 120, sm: 140 },
                          width: 'auto',
                          mt: 1,
                          '& .MuiMenuItem-root': {
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1, sm: 1.5 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText',
                            },
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, textAlign: 'left' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {user?.fullName ?? 'My Account'}
                      </Typography>
                      {user?.primaryEmailAddress?.emailAddress && (
                        <Typography variant="body2" color="text.secondary">
                          {user.primaryEmailAddress.emailAddress}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {subscriptionTier} plan
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={handleMyAccount}>
                      <ListItemIcon>
                        <AccountCircle fontSize="small" />
                      </ListItemIcon>
                      My Account
                    </MenuItem>
                    {!isProUser && (
                      <MenuItem onClick={handleUpgrade}>
                        <ListItemIcon>
                          <WorkspacePremium fontSize="small" />
                        </ListItemIcon>
                        Upgrade
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleSignOut}>
                      <ListItemIcon>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      Sign Out
                    </MenuItem>
                  </Menu>
                </Box>
              </SignedIn>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        checkoutUrl={checkoutUrl}
        loading={loadingUser}
        error={userError}
      />
    </>
  );
};

export default Header;
